import {
  type WorkflowQueueAction,
  workflowQueueActions,
} from "@/components/shared/workflow-queue-actions";
import { countLabel } from "@/components/ui";
import type { ProgrammeSummary, SessionContext } from "@/platform";
import type { ReadinessState, WorkflowState } from "@/server/types";

export function getWorkflowActions(
  session: SessionContext,
  queueProgramme: ProgrammeSummary,
  submissionBlocked: boolean,
  submissionBlockerCount: number,
  approvalBlocked: boolean,
  approvalBlockers: unknown[],
  blockingReviewFindingCount: number,
  currentRoundReviewerUserIds: readonly string[],
  /** An active reviewer is assigned, so a submission goes to internal review. */
  hasInternalReviewer = true,
): WorkflowQueueAction[] {
  return workflowQueueActions(
    session,
    queueProgramme,
    currentRoundReviewerUserIds,
    hasInternalReviewer,
  ).map((action) => {
    if (action.id === "submit-review" && submissionBlocked) {
      return {
        ...action,
        blockedReason:
          submissionBlockerCount > 0
            ? `${countLabel(submissionBlockerCount, "outstanding requirement")} must be completed before submission.`
            : "Programme readiness checks must pass before submission.",
      };
    }
    if (action.id === "approve" && approvalBlocked) {
      return {
        ...action,
        blockedReason:
          approvalBlockers.length > 0
            ? `${countLabel(approvalBlockers.length, "outstanding requirement")} must be completed before approval.`
            : "Programme readiness checks must pass before approval.",
      };
    }
    if (action.id === "mark-reviewed" && blockingReviewFindingCount > 0) {
      return {
        ...action,
        blockedReason: `${countLabel(blockingReviewFindingCount, "blocking review finding")} must be resolved first.`,
      };
    }
    return action;
  });
}

/** Final sign-off decisions, offered beside the review ones so every stage reads alike. */
export function finalSignOffActions({
  mayLead,
  mayQa,
  mayComplete,
  signOffsRecorded,
}: {
  mayLead: boolean;
  mayQa: boolean;
  mayComplete: boolean;
  signOffsRecorded: boolean;
}): WorkflowQueueAction[] {
  const actions: WorkflowQueueAction[] = [];
  if (mayLead) {
    actions.push({
      id: "sign-off-lead",
      label: "Sign off as lead",
      icon: "seal-check",
      requiresNote: true,
      tone: "outline",
    });
  }
  if (mayQa) {
    actions.push({
      id: "sign-off-qa",
      label: "Sign off as QA",
      icon: "shield-check",
      requiresNote: true,
      tone: "outline",
    });
  }
  if (mayComplete) {
    actions.push({
      id: "complete-final-sign-off",
      label: "Complete final descriptor sign-off",
      icon: "stamp",
      nextState: "Final Descriptor Signed Off",
      requiresNote: true,
      tone: "primary",
      ...(signOffsRecorded
        ? {}
        : { blockedReason: "Record the Programme Lead and QA sign-offs first." }),
    });
  }
  return actions;
}

/** Why the final export is unavailable, mirroring the server's own checks. */
export function finalExportRefusal(
  isLead: boolean,
  workflowState: WorkflowState,
  readiness: ReadinessState | undefined,
): string | undefined {
  if (readiness !== "Ready for final export") {
    return "Programme readiness checks must pass before the final export.";
  }
  if (workflowState !== "Final Descriptor Signed Off") {
    return "Complete the final descriptor sign-off before exporting.";
  }
  if (!isLead) {
    return "Only the Programme Lead can run the final export.";
  }
  return undefined;
}
